import React from "react";
import {
  Card,
  CardContent,
  Avatar,
  Typography,
  Box,
  Tooltip,
  IconButton,
} from "@material-ui/core";
import PersonIcon from "@material-ui/icons/Person";
import CallOutlinedIcon from "@material-ui/icons/CallOutlined";
import VideoCallOutlinedIcon from "@material-ui/icons/VideoCallOutlined";
import Chat from "@material-ui/icons/Chat";
import { useCall } from "../../../context/CallContext";
import { useChat } from "../../../context/ChatContext";
import { useMutualLikes } from "../../../context/MutualLikesContext";
import Configs from "../../../utils/Configs";

const BASE_URL = Configs.baseUrl;

function toTitleCase(str) {
  return str.replace(
    /\w\S*/g,
    (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  );
}

function truncateText(text, maxLength = 40) {
  if (!text) return "";
  return text.length > maxLength ? text.substring(0, maxLength) + "…" : text;
}

// ✅ Updated formatTime to show both date and time consistently
function formatTime(timestamp) {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  const now = new Date();

  const isToday = date.toDateString() === now.toDateString();
  const yesterday = new Date();
  yesterday.setDate(now.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();

  const timeString = date.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });

  if (isToday) {
    return `Today at ${timeString}`;
  }

  if (isYesterday) {
    return `Yesterday at ${timeString}`;
  }

  // If older than yesterday, show Date + Time
  const dateOptions = {
    month: "short",
    day: "numeric",
    // Show year only if it's not the current year
    ...(date.getFullYear() !== now.getFullYear() && { year: "numeric" }),
  };

  const dateString = date.toLocaleDateString(undefined, dateOptions);
  return `${dateString}, ${timeString}`;
}

function ChatCard({ conversation, onSelect }) {
  const { openCall, openVoiceCall } = useCall();
  const { openChat } = useChat();
  const { mutualLikes } = useMutualLikes();

  const isMutualLike = mutualLikes.includes(conversation.participant_user_id);

  const handleChatClick = (e) => {
    e.stopPropagation();
    onSelect(conversation);
  };

  const handleVoiceCallClick = (e) => {
    e.stopPropagation();
    if (!isMutualLike) return;
    openVoiceCall({
      id: conversation.participant_user_id,
      name: conversation.participant_name,
      avatar: `${BASE_URL}${conversation.participant_profile_pic}`,
    });
  };

  const handleVideoCallClick = (e) => {
    e.stopPropagation();
    if (!isMutualLike) return;
    openCall({
      id: conversation.participant_user_id,
      name: conversation.participant_name,
      avatar: `${BASE_URL}${conversation.participant_profile_pic}`,
    });
  };

  return (
    <Card
      style={{
        backgroundColor: "#fff",
        marginBottom: 12,
        borderRadius: 12,
        boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)",
        transition: "transform 0.15s ease-in-out",
      }}
      onMouseOver={(e) => (e.currentTarget.style.transform = "scale(1.01)")}
      onMouseOut={(e) => (e.currentTarget.style.transform = "scale(1)")}
    >
      <CardContent
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 10px",
        }}
      >
        <Box display="flex" alignItems="center" style={{ flex: 1 }}>
          <Avatar
            src={
              conversation.participant_profile_pic
                ? `${BASE_URL}${conversation.participant_profile_pic}`
                : undefined
            }
            alt={conversation.participant_name}
            style={{ width: 56, height: 56, marginRight: 16 }}
          >
            {!conversation.participant_profile_pic && <PersonIcon />}
          </Avatar>
          <Box>
            <Typography style={{ fontWeight: 600 }}>
              {toTitleCase(conversation.participant_name)}
            </Typography>
            <Typography variant="body2" style={{ color: "#666" }}>
              {truncateText(conversation.last_message_body)}
            </Typography>
            <Typography variant="caption" style={{ color: "#aaa" }}>
              {formatTime(conversation.last_message_time)}
            </Typography>
          </Box>
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          <Tooltip title="Chat" placement="top">
            <IconButton size="small" onClick={handleChatClick}>
              <Chat style={{ color: "#4CAF50" }} fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip
            title={
              isMutualLike
                ? "Voice Call"
                : "Only mutual likes can call each other"
            }
            placement="top"
          >
            <span>
              <IconButton
                size="small"
                onClick={handleVoiceCallClick}
                disabled={!isMutualLike}
              >
                <CallOutlinedIcon
                  fontSize="small"
                  style={{
                    color: isMutualLike ? "#4CAF50" : "#B0B0B0",
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip
            title={
              isMutualLike
                ? "Video Call"
                : "Only mutual likes can call each other"
            }
            placement="top"
          >
            <span>
              <IconButton
                size="small"
                onClick={handleVideoCallClick}
                disabled={!isMutualLike}
              >
                <VideoCallOutlinedIcon
                  fontSize="small"
                  style={{
                    color: isMutualLike ? "#4CAF50" : "#B0B0B0",
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </CardContent>
    </Card>
  );
}

export default ChatCard;
